<template>
  <div>
    <v-row no-gutters class="uniform-gap-32">
      <v-col cols="12" class="grid-col">
        <v-card class="rounded-lg elevation-0 mb-6">
          <v-card-title class="py-4 d-flex">
            <div class="card_title">
              <h3 class="tabletitle pb-0">
                {{ $store.getters.t ? $store.getters.t('social.integration.add_channel') : '채널 추가' }}
              </h3>
              <div class="section-sub">
                {{ $store.getters.t ? $store.getters.t('social.integration.add_channel_description') : '영상 업로드 / 라이브 스트리밍을 소셜 미디어에 연동하고 관리 할 수 있습니다.' }}
              </div>
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-progress-linear
              v-if="loading && !platforms.length"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <div v-if="platforms.length" class="platform-select">
              <v-hover
                v-for="platform in platforms"
                :key="platform.id"
                v-slot="{ hover }"
              >
                <v-card
                  class="pa-3 platform-card"
                  outlined
                  :elevation="hover ? 4 : 0"
                  :disabled="platform.name.toLowerCase() === 'instagram'"
                  :class="{ 'platform-card--hover': hover }"
                  @click="connectPlatform(platform)"
                >
                <v-chip v-if="platform.name.toLowerCase() === 'instagram'" label color="gray" x-small class="chip_posi">
                    {{ $store.getters.t('common.status.preparing') }}
                </v-chip>
                  <div class="platform-body">
                    <div
                      class="platform-icon-wrap"
                      :class="platformIconWrapClass(platform)"
                    >
                      <v-img
                        :src="platformImage(platform)"
                        class="platform-icon"
                        contain
                      />
                    </div>
                    <div class="platform-text">
                      <div class="platform-label">
                        {{ platform.name }}
                      </div>
                      <div class="platform-desc">
                        {{ platformDescription(platform) }}
                      </div>
                    </div>
                    <v-icon class="ml-auto arrow-icon">mdi-chevron-right</v-icon>
                  </div>
                </v-card>
              </v-hover>
            </div>

            <div v-else-if="!loading" class="no-data-form">
              <v-icon small>mdi-alert-circle-outline</v-icon>
              <span>{{ $store.getters.t ? $store.getters.t('social.integration.no_platforms') : '등록된 플랫폼이 없습니다. 관리자에게 문의하세요.' }}</span>
            </div>
            

          </v-card-text>
        </v-card>

        <v-card class="rounded-lg elevation-0 mb-6">
          <v-card-title class="py-4 d-flex">
            <div class="card_title">
              <h3 class="tabletitle pb-0">
                {{ $store.getters.t ? $store.getters.t('social.integration.registered_channels') : '등록된 채널' }}
              </h3>
              <div class="section-sub">
                {{ $store.getters.t ? $store.getters.t('social.integration.registered_description') : '연결 상태를 확인하고 재연결/해제를 관리하세요.' }}
              </div>
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-progress-linear
              v-if="loading && !channelCards.length"
              indeterminate
              color="primary"
              class="mb-4"
            />
            
            <div v-if="!loading && !channelCards.length" class="no-data-form">
              <v-icon small>mdi-alert-circle-outline</v-icon>
              <span>{{ $store.getters.t ? $store.getters.t('social.integration.no_channels') : '연동된 채널이 없습니다.' }}</span>
            </div>

            <v-row
              v-else
              dense
              no-gutters
              class="platform-select"
            >
              <v-col
                v-for="ch in channelCards"
                :key="ch.id"
                class="custom-col"
              >
                <v-card
                  outlined
                  class="rounded-lg"
                  :style="!ch.connected ? 'border-color:#fb923c;background:#fff7ed;' : null"
                >
                  <v-card-text>
                    <div class="d-flex align-center mb-4">
                      <div class="sm-avatar-wrap">
                        <v-img
                          :src="ch.thumb"
                          width="48"
                          height="48"
                          class="sm-avatar"
                          contain
                        />
                        <v-img
                          :src="platformIcon(ch.platform)"
                          width="16"
                          height="16"
                          class="platform-badge"
                          contain
                        />
                        <div v-if="!ch.connected" class="sm-disconnect-dot" />
                      </div>
                      <div class="flex-grow-1 ml-4">
                        <div class="font-weight-bold black--text">
                          {{ ch.name }}
                        </div>
                        <div class="caption grey--text">
                          <a
                            v-if="ch.channelUrl"
                            :href="channelLinkHref(ch.channelUrl)"
                            class="channel-account-link"
                            target="_blank"
                            rel="noopener"
                          >
                            {{ truncateText(ch.channelUrl) }}
                          </a>
                          <span v-else>
                            {{ ch.account ? truncateText(ch.account) : '-' }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      class="pa-3 mb-4 sm-status"
                      :style="!ch.connected ? 'border-radius:8px;background:#ffedd5;border:1px solid #fed7aa;' : null"
                    >
                      <template v-if="!ch.connected">
                        <div class="d-flex align-center">
                          <v-icon small :color="ch.statusColor" class="mr-2">
                            mdi-alert
                          </v-icon>
                          <span
                            class="caption font-weight-bold"
                            :style="{ color: ch.statusColor }"
                          >
                            {{ ch.statusLabel }}
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <div class="d-flex align-center justify-space-between">
                          <span class="caption font-weight-medium">
                            {{ $store.getters.t ? $store.getters.t('social.history.connection.title') : '연결 상태' }}
                          </span>
                          <div class="d-flex align-center">
                            <span class="status-dot mr-2" style="background:#10b981;" />
                            <span class="caption font-weight-bold" style="color:#047857;">
                              {{ ch.statusLabel }}
                            </span>
                          </div>
                        </div>
                      </template>
                    </div>

                    <!-- <div
                      class="caption grey--text"
                      v-if="ch.tokenExpiresAt && ch.tokenExpiresAt !== '-'"
                    >
                      {{ $store.getters.t ? $store.getters.t('social.integration.token.expired') : '토큰 만료' }}:
                      {{ ch.tokenExpiresAt }}
                    </div>
                    <div
                      class="caption grey--text"
                      v-if="ch.updatedAt && ch.updatedAt !== '-'"
                    >
                      {{ $store.getters.t ? $store.getters.t('social.integration.metadata.last_updated') : '최근 갱신' }}:
                      {{ ch.updatedAt }}
                    </div> -->

                    <!-- <div class="d-flex align-center mt-3">
                      <v-btn
                        text
                        small
                        color="primary"
                        class="px-0"
                        @click="openEditById(ch.id)"
                      >
                        <v-icon left small>mdi-pencil</v-icon>
                        {{ $store.getters.t ? $store.getters.t('social.integration.action.edit') : '정보 수정' }}
                      </v-btn>
                      <v-spacer />
                    </div> -->

                    <div class="d-flex mt-3">
                      <template v-if="!ch.connected">
                        <v-btn
                          depressed
                          class="flex-grow-1 mr-1"
                          color="#f97316"
                          dark
                          :loading="isButtonLoading(ch.id, 'reconnect')"
                          :disabled="isButtonLoading(ch.id, 'reconnect') || loading"
                          @click="reconnectById(ch.id)"
                        >
                          <v-icon small class="mr-1">mdi-refresh</v-icon>
                          {{ $store.getters.t ? $store.getters.t('social.integration.action.reconnect') : '채널 재연결' }}
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          depressed
                          class="flex-grow-1 mr-1"
                          color="grey darken-2"
                          dark
                          :loading="isButtonLoading(ch.id, 'disconnect')"
                          :disabled="isButtonLoading(ch.id, 'disconnect') || loading"
                          @click="unlinkById(ch.id)"
                        >
                          {{ $store.getters.t ? $store.getters.t('social.integration.action.disconnect') : '연결 해제' }}
                        </v-btn>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!--정보수정 모달창-->
    <!-- <v-dialog
      v-model="dialog"
      max-width="640"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="headline">
          {{ dialogTitle }}
        </v-card-title>
        <v-card-text class="integration-form-body">
          <v-form ref="form" v-model="formValid">
            <v-alert dense outlined type="info" class="mb-4">
              {{
                dialogMode === 'create'
                ? ($store.getters.t ? $store.getters.t('social.integration.form.create_hint') : '플랫폼에서 발급받은 채널 정보와 토큰을 입력해주세요.')
                : ($store.getters.t ? $store.getters.t('social.integration.form.edit_hint') : '수정하지 않을 항목은 비워두면 기존 값이 유지됩니다.')
              }}
            </v-alert>
            <v-text-field
              v-if="dialogMode === 'create'"
              :label="$store.getters.t ? $store.getters.t('social.upload.summary.platform') : '플랫폼'"
              :value="selectedPlatform?.name"
              outlined
              dense
              disabled
            />
            <v-text-field
              v-model="form.external_name"
              :label="$store.getters.t ? $store.getters.t('social.integration.form.channel_name') : '채널 이름'"
              :rules="[rules.required]"
              outlined
              dense
              clearable
            />
            <v-text-field
              v-model="form.external_id"
              :label="$store.getters.t ? $store.getters.t('social.integration.form.channel_id') : '채널 ID / 식별자'"
              :rules="[rules.required]"
              outlined
              dense
              clearable
            />
            <v-text-field
              v-model="form.channel_url"
              :label="$store.getters.t ? $store.getters.t('social.integration.form.channel_url') : '채널 URL'"
              outlined
              dense
              clearable
            />
            <v-select
              v-model="form.status"
              :items="statusOptions"
              :label="$store.getters.t ? $store.getters.t('social.history.connection.title') : '연동 상태'"
              outlined
              dense
            />
            <v-text-field
              v-model="form.access_token"
              :label="$store.getters.t ? $store.getters.t('social.integration.form.access_token') : 'Access Token'"
              outlined
              dense
              clearable
              type="password"
            />
            <v-text-field
              v-model="form.refresh_token"
              :label="$store.getters.t ? $store.getters.t('social.integration.form.refresh_token') : 'Refresh Token'"
              outlined
              dense
              clearable
              type="password"
            />
            <v-text-field
              v-model="form.token_expires_at"
              :label="$store.getters.t ? $store.getters.t('social.integration.form.token_expiry') : '토큰 만료일 (ISO8601)'"
              outlined
              dense
              placeholder="2025-02-20T12:00:00+09:00"
            />
            <v-textarea
              v-model="form.notes"
              :label="$store.getters.t ? $store.getters.t('common.label.memo') : '비고'"
              auto-grow
              outlined
              rows="2"
              dense
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="integration-form-actions">
          <v-spacer />
          <v-btn text @click="closeDialog">
            {{ $store.getters.t ? $store.getters.t('common.action.cancel') : '취소' }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="actionLoading"
            :disabled="actionLoading"
            @click="submitForm"
          >
            <v-icon left small>mdi-content-save</v-icon>
            {{
              dialogMode === 'create'
                ? ($store.getters.t ? $store.getters.t('social.integration.action.save_connection') : '연동 저장')
                : ($store.getters.t ? $store.getters.t('common.action.save_changes') : '정보 저장')
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> -->

    <!--로딩 오버레이 필요시 사용-->
    <!-- <v-overlay :value="loading" absolute opacity="0.12">
      <v-progress-circular indeterminate color="primary" size="48" />
    </v-overlay> -->

    <v-overlay :value="oauthInProgress" absolute opacity="0.18">
      <v-card elevation="2" class="pa-5 text-center" width="320">
        <v-progress-circular indeterminate color="primary" size="36" class="mb-3" />
        <div class="text-body-2">
          {{ $store.getters.t ? $store.getters.t('social.integration.auth.browser_prompt') : '브라우저 창에서 인증을 완료해주세요.' }}
        </div>
      </v-card>
    </v-overlay>

    <AppSnackbar
      :message="snackbarText"
      :visible="snackbar"
      :error="snackbarError"
    />
  </div>
</template>

<script>
import SocialApi from '@/api/social/SocialApi';
import AppSnackbar from '@/components/ui/snackbar/Snackbar.vue';

export default {
  name: 'SocialConnect',
  components: { AppSnackbar },
  data() {
    return {
      loading: false,
      actionLoading: false,
      platforms: [
      ],
      integrations: [],
      snackbar: false,
      snackbarText: '',
      snackbarError: false,
      dialog: false,
      dialogMode: 'create',
      selectedPlatform: null,
      editingIntegration: null,
      formValid: false,
      form: this.getDefaultForm(),
      oauthInProgress: false,
      oauthPlatform: null,
      oauthPopup: null,
      oauthTimer: null,
      buttonState: { id: null, type: null },
      rules: {
        required: (value) =>
          !!value ||
          (this.$store.getters.t
            ? this.$store.getters.t('common.validation.required')
            : '필수항목입니다.'),
      },
      statusOptions: [
        { text: this.$store.getters.t ? this.$store.getters.t('social.history.connection.connected') : '연결됨', value: 'active' },
        { text: this.$store.getters.t ? this.$store.getters.t('social.integration.status.requires_reconnect') : '재연결 필요', value: 'requires_reconnect' },
        { text: this.$store.getters.t ? this.$store.getters.t('social.integration.token.expired') : '토큰 만료', value: 'token_expired' },
        { text: this.$store.getters.t ? this.$store.getters.t('social.history.connection.disconnected') : '연결 해제', value: 'disconnected' },
        { text: this.$store.getters.t ? this.$store.getters.t('social.integration.status.disabled') : '사용 안함', value: 'disabled' },
      ],
      defaultThumb: require('@/styles/socialimage.png'),
      platformIconMap: {
        youtube: require('@/styles/youtube.png'),
        instagram: require('@/styles/instgram.png'),
        default: require('@/styles/socialimage_color.png'),
      },
      snackbarTimer: null,
    };
  },
  computed: {
    dialogTitle() {
      if (this.dialogMode === 'create') {
        return this.$store.getters.t
          ? this.$store.getters.t('social.integration.drawer.create_title')
          : '새 채널 연동';
      }
      return this.$store.getters.t
        ? this.$store.getters.t('social.integration.drawer.edit_title')
        : '연동 정보 수정';
    },
    channelCards() {
      const nameFallback = this.$store.getters.t
        ? this.$store.getters.t('common.label.untitled')
        : '이름 없음';
      return (this.integrations || []).map((integration) => {
        const platformKey = (integration.platform?.key || '').toLowerCase();
        const connected = integration.status === 'active';
        return {
          id: integration.id,
          platform: platformKey || 'default',
          connected,
          name:
            integration.external_name ||
            integration.platform?.name ||
            nameFallback,
          channelUrl: this.channelLink(integration),
          account: this.channelAccount(integration),
          thumb: integration.settings?.thumbnail_url || this.defaultThumb,
          status: integration.status,
          statusLabel: this.statusLabel(integration.status),
          statusColor: this.statusColor(integration.status),
          tokenExpiresAt: this.formatDate(integration.token_expires_at),
          updatedAt: this.formatDate(integration.updated_at),
          canDisconnect:
            typeof integration.can_disconnect === 'boolean'
              ? integration.can_disconnect
              : true,
        };
      });
    },
  },
  created() {
    this.initialize();
  },
  mounted() {
    window.addEventListener('message', this.handleOAuthMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleOAuthMessage);
    this.clearOAuthState();
    if (this.snackbarTimer) {
      window.clearTimeout(this.snackbarTimer);
    }
  },
  methods: {
    getDefaultForm() {
      return {
        external_name: '',
        external_id: '',
        channel_url: '',
        status: 'active',
        access_token: '',
        refresh_token: '',
        token_expires_at: '',
        notes: '',
      };
    },
    async initialize() {
      this.loading = true;
      try {
        await Promise.all([this.fetchPlatforms(), this.fetchIntegrations()]);
      } catch (error) {
        this.handleError(
          error,
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.load_settings')
            : '설정 정보를 불러오지 못했습니다.'
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchPlatforms() {
      const data = await SocialApi.fetchPlatforms();
      this.platforms = Array.isArray(data) ? data : [];
    },
    async fetchIntegrations() {
      const data = await SocialApi.fetchIntegrations({ include_inactive: true });
      this.integrations = Array.isArray(data) ? data : [];
    },
    connectPlatform(platform) {
      if (!platform) {
        return;
      }
      this.openCreate(platform);
    },
    connectPlatformByKey(key) {
      const platform = this.platforms.find(
        (item) => (item.key || '').toLowerCase() === key
      );
      if (!platform) {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.unsupported_platform')
            : '지원되지 않는 플랫폼입니다.',
          true
        );
        return;
      }
      this.openCreate(platform);
    },
    connectYouTube() {
      this.connectPlatformByKey('youtube');
    },
    connectInstagram() {
      this.connectPlatformByKey('instagram');
    },
    openCreate(platform) {
      if (!platform) {
        return;
      }
      if ((platform.auth_strategy || '').toLowerCase() === 'oauth') {
        this.beginOAuth(platform);
        return;
      }

      this.dialogMode = 'create';
      this.selectedPlatform = platform;
      this.editingIntegration = null;
      this.form = this.getDefaultForm();
      this.dialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    openEditById(id) {
      const integration = this.findIntegrationById(id);
      if (!integration) {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.not_found')
            : '연동 정보를 찾을 수 없습니다.',
          true
        );
        return;
      }
      this.openEdit(integration);
    },
    openEdit(integration) {
      this.dialogMode = 'edit';
      this.selectedPlatform = integration.platform;
      this.editingIntegration = integration;
      this.form = {
        external_name: integration.external_name || '',
        external_id: integration.external_id || '',
        channel_url: integration.channel_url || '',
        status: integration.status || 'active',
        access_token: '',
        refresh_token: '',
        token_expires_at: integration.token_expires_at || '',
        notes: integration.settings?.notes || '',
      };
      this.dialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    closeDialog() {
      this.dialog = false;
      this.actionLoading = false;
    },
    async submitForm() {
      if (!this.$refs.form || !this.$refs.form.validate()) {
        return;
      }
      this.actionLoading = true;
      try {
        if (this.dialogMode === 'create') {
          await this.handleCreate();
        } else {
          await this.handleUpdate();
        }
        this.closeDialog();
        await this.fetchIntegrations();
      } catch (error) {
        this.handleError(
          error,
          this.$store.getters.t
            ? this.$store.getters.t('common.error.request_failed')
            : '요청을 처리하지 못했습니다.'
        );
      } finally {
        this.actionLoading = false;
      }
    },
    buildPayload() {
      const payload = {
        external_name: this.form.external_name?.trim(),
        external_id: this.form.external_id?.trim(),
        channel_url: this.form.channel_url?.trim() || null,
        status: this.form.status,
      };

      if (this.form.access_token) {
        payload.access_token = this.form.access_token.trim();
      }
      if (this.form.refresh_token) {
        payload.refresh_token = this.form.refresh_token.trim();
      }
      if (this.form.token_expires_at) {
        payload.token_expires_at = this.form.token_expires_at.trim();
      }
      if (this.form.notes) {
        payload.settings = {
          notes: this.form.notes.trim(),
        };
      }

      return payload;
    },
    async handleCreate() {
      const payload = this.buildPayload();
      payload.platform_id = this.selectedPlatform.id;
      await SocialApi.createIntegration(payload);
      this.showSnackbar(
        this.$store.getters.t
          ? this.$store.getters.t('social.integration.toast.created')
          : '채널이 성공적으로 연동되었습니다.'
      );
    },
    async handleUpdate() {
      const payload = this.buildPayload();
      await SocialApi.updateIntegration(this.editingIntegration.id, payload);
      this.showSnackbar(
        this.$store.getters.t
          ? this.$store.getters.t('social.integration.toast.updated')
          : '채널 정보를 업데이트했습니다.'
      );
    },
    async reconnectById(id) {
      const integration = this.findIntegrationById(id);
      if (!integration) {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.not_found')
            : '연동 정보를 찾을 수 없습니다.',
          true
        );
        return;
      }
      this.setButtonLoading(id, 'reconnect');
      try {
        await this.reconnectIntegration(integration);
      } finally {
        this.clearButtonLoading();
      }
    },
    async unlinkById(id) {
      const integration = this.findIntegrationById(id);
      if (!integration) {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.not_found')
            : '연동 정보를 찾을 수 없습니다.',
          true
        );
        return;
      }
      if (integration.status === 'disconnected') {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.already_disconnected')
            : '이미 연결 해제된 채널입니다.',
          true
        );
        return;
      }
      if (!integration.can_disconnect) {
        window.alert(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.live_disconnect_forbidden')
            : '라이브 송출 중인 채널은 연결 해제할 수 없습니다. 먼저 송출을 중지해주세요.'
        );
        return;
      }
      const confirmed = window.confirm(
        this.$store.getters.t
          ? this.$store.getters.t('social.integration.confirm.disconnect')
          : '연결을 해제하면 자동 업로드 및 송출이 중단됩니다. 계속하시겠습니까?'
      );
      if (!confirmed) {
        return;
      }

      this.setButtonLoading(id, 'disconnect');
      try {
        await SocialApi.deleteIntegration(integration.id);
        await this.fetchIntegrations();
      this.showSnackbar(
        this.$store.getters.t
          ? this.$store.getters.t('social.integration.toast.disconnected')
          : '채널 연결을 해제했습니다.'
      );
      } catch (error) {
        this.handleError(
          error,
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.disconnect_failed')
            : '연결 해제에 실패했습니다.'
        );
      } finally {
        this.clearButtonLoading();
      }
    },
    async reconnectIntegration(integration) {
      if (!integration?.platform) {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.platform_not_found')
            : '플랫폼 정보를 찾을 수 없습니다.',
          true
        );
        return;
      }
      await this.beginOAuth(integration.platform);
    },
    async beginOAuth(platform) {
      if (this.oauthInProgress) {
        if (this.oauthPopup && !this.oauthPopup.closed) {
          this.oauthPopup.focus();
        }
        return;
      }

      try {
        this.oauthInProgress = true;
        this.oauthPlatform = platform;

        const response = await SocialApi.beginOAuth(platform.key);
        const url = response?.authorization_url;
        if (!url) {
          throw new Error(
            this.$store.getters.t
              ? this.$store.getters.t('social.integration.error.oauth_url')
              : '연동 URL을 가져오지 못했습니다.'
          );
        }

        const features =
          'width=520,height=720,menubar=no,toolbar=no,location=no,status=no';
        this.oauthPopup = window.open(
          url,
          'streamonSocialOAuth',
          features
        );

        if (!this.oauthPopup || this.oauthPopup.closed) {
          throw new Error(
            this.$store.getters.t
              ? this.$store.getters.t('social.integration.error.popup_blocked')
              : '팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.'
          );
        }

        this.oauthTimer = window.setInterval(() => {
          if (!this.oauthPopup || this.oauthPopup.closed) {
            this.handleOAuthWindowClosed();
          }
        }, 700);
      } catch (error) {
        this.clearOAuthState();
        this.handleError(
          error,
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.start_failed')
            : '연동을 시작하지 못했습니다.'
        );
      }
    },
    async handleOAuthMessage(event) {
      const payload = event?.data;
      if (!payload || payload.type !== 'social-oauth-complete') {
        return;
      }

      if (this.oauthTimer) {
        window.clearInterval(this.oauthTimer);
        this.oauthTimer = null;
      }

      this.oauthInProgress = false;
      this.oauthPlatform = null;

      if (this.oauthPopup && !this.oauthPopup.closed) {
        this.oauthPopup.close();
      }
      this.oauthPopup = null;

      if (payload.status === 'success') {
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.toast.created')
            : '채널이 성공적으로 연동되었습니다.'
        );
        await this.fetchIntegrations();
      } else {
        const message =
          payload.message ||
          (this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.connection_failed')
            : '연동에 실패했습니다. 다시 시도해주세요.');
        this.showSnackbar(message, true);
      }

      this.clearOAuthState();
    },
    handleOAuthWindowClosed() {
      if (this.oauthTimer) {
        window.clearInterval(this.oauthTimer);
        this.oauthTimer = null;
      }

      if (this.oauthPopup && !this.oauthPopup.closed) {
        this.oauthPopup.close();
      }
      this.oauthPopup = null;
      this.oauthPlatform = null;

      if (this.oauthInProgress) {
        this.oauthInProgress = false;
        this.showSnackbar(
          this.$store.getters.t
            ? this.$store.getters.t('social.integration.error.window_closed')
            : '연동 창이 닫혔습니다. 다시 시도해주세요.',
          true
        );
      }

      this.clearOAuthState();
    },
    clearOAuthState() {
      if (this.oauthTimer) {
        window.clearInterval(this.oauthTimer);
        this.oauthTimer = null;
      }
      if (this.oauthPopup && !this.oauthPopup.closed) {
        this.oauthPopup.close();
      }
      this.oauthPopup = null;
      this.oauthPlatform = null;
      this.oauthInProgress = false;
    },
    showSnackbar(message, isError = false) {
      this.snackbarText = message;
      this.snackbarError = isError;
      this.snackbar = true;
      if (this.snackbarTimer) {
        window.clearTimeout(this.snackbarTimer);
      }
      this.snackbarTimer = window.setTimeout(() => {
        this.snackbar = false;
        this.snackbarTimer = null;
      }, 4000);
    },
    handleError(error, fallbackMessage) {
      console.error('[SocialConnectForm] error', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        fallbackMessage ||
        (this.$store.getters.t
          ? this.$store.getters.t('common.error.unknown')
          : '알 수 없는 오류가 발생했습니다.');
      this.showSnackbar(message, true);
    },
    isButtonLoading(id, type) {
      return this.buttonState.id === id && this.buttonState.type === type;
    },
    setButtonLoading(id, type) {
      this.buttonState = { id, type };
    },
    clearButtonLoading() {
      this.buttonState = { id: null, type: null };
    },
    findIntegrationById(id) {
      return this.integrations.find((integration) => integration.id === id);
    },
    channelLink(integration) {
      return (
        integration.channel_url ||
        integration.settings?.channel_url ||
        integration.credentials?.channel_url ||
        ''
      );
    },
    channelAccount(integration) {
      return (
        integration.external_id ||
        integration.credentials?.email ||
        integration.settings?.channel_id ||
        '-'
      );
    },
    truncateText(value, limit = 35) {
      if (!value || typeof value !== 'string') {
        return '';
      }
      const trimmed = value.trim();
      if (!trimmed) {
        return '';
      }
      return trimmed.length > limit ? `${trimmed.slice(0, limit)}...` : trimmed;
    },
    channelLinkHref(link) {
      if (!link || typeof link !== 'string') {
        return '';
      }
      const trimmed = link.trim();
      if (!trimmed) {
        return '';
      }
      return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    },
    statusColor(status) {
      switch (status) {
        case 'active':
          return '#047857';
        case 'requires_reconnect':
          return '#b45309';
        case 'token_expired':
          return '#b91c1c';
        case 'disconnected':
          return '#f97316';
        case 'disabled':
          return '#6b7280';
        default:
          return '#6b7280';
      }
    },
    statusLabel(status) {
      switch (status) {
        case 'active':
          return this.$store.getters.t
            ? this.$store.getters.t('social.history.connection.connected')
            : '연결됨';
        case 'disconnected':
          return this.$store.getters.t
            ? this.$store.getters.t('social.history.connection.disconnected')
            : '연결 해제';
        case 'requires_reconnect':
          return this.$store.getters.t
            ? this.$store.getters.t('social.integration.status.requires_reconnect')
            : '재연결 필요';
        case 'token_expired':
          return this.$store.getters.t
            ? this.$store.getters.t('social.integration.token.expired')
            : '토큰 만료';
        case 'disabled':
          return this.$store.getters.t
            ? this.$store.getters.t('social.integration.status.disabled')
            : '사용 안함';
        default:
          return status || (this.$store.getters.t
            ? this.$store.getters.t('common.label.unknown')
            : '알 수 없음');
      }
    },
    formatDate(value) {
      if (!value) {
        return '-';
      }
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return value;
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (error) {
        return value;
      }
    },
    platformImage(platform) {
      const key = (platform?.key || '').toLowerCase();
      return this.platformIconMap[key] || this.platformIconMap.default;
    },
    platformIcon(key) {
      return this.platformIconMap[key] || this.platformIconMap.default;
    },
    platformIconWrapClass(platform) {
      const key = (platform?.key || '').toLowerCase();
      return key === 'instagram' ? 'gradient' : null;
    },
    platformDescription(platform) {
      if (platform?.description) {
        return platform.description;
      }
      const supportsVod = platform?.supports_vod;
      const supportsLive = platform?.supports_live;
      if (supportsVod && supportsLive) {
        return this.$store.getters.t
          ? this.$store.getters.t('social.integration.capability.upload_and_live')
          : '영상 업로드 · 라이브 지원';
      }
      if (supportsVod) {
        return this.$store.getters.t
          ? this.$store.getters.t('social.integration.capability.upload_only')
          : '영상 업로드 지원';
      }
      if (supportsLive) {
        return this.$store.getters.t
          ? this.$store.getters.t('social.integration.capability.live_only')
          : '라이브 송출 지원';
      }
      return this.$store.getters.t
        ? this.$store.getters.t('social.integration.add_channel')
        : '채널 추가';
    },
  },
};
</script>

<style scoped>
.section-head { margin-bottom: 12px; }
.section-title {
  display: flex; align-items: center; font-weight: 800; font-size: 16px;
  color: #111827;
}
.section-sub {
  font-size: 12px; color: #777777;
}

.guide-banner {
  border-color: #c7e4ff !important;
  background: #f3f9ff !important;
  color: #0b4a7a !important;
}

.soft-divider { opacity: .6; }

.platform-select {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.platform-card {
  cursor: pointer;
  border-radius: 12px !important;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
  border-color: #e5e7eb !important;
  position: relative;
}
.platform-card--hover { transform: translateY(-1px); border-color: #cbd5e1 !important; }
.platform-card--disabled { opacity: 0.6; pointer-events: none; }
.platform-body { display: flex; align-items: center; gap: 12px; }

.platform-icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.platform-icon-wrap.gradient {
  background: none;
}
.platform-icon { width: 40px; height: 40px; }

.platform-text { display: flex; flex-direction: column; }
.platform-label { font-weight: 800; line-height: 1.1; color: #111827; }
.platform-desc  { font-size: 12px; color: #6b7280; margin-top: 2px; }
.arrow-icon { color: #9ca3af; }

.sm-status{ border-radius:8px;background:#f9fafb; }
.sm-logo{ width:48px;height:48px;border-radius:12px;display:flex;align-items:center;justify-content:center; }
.sm-logo.youtube{ background:#dc2626; }
.sm-logo.insta{ background:linear-gradient(135deg,#8b5cf6,#ec4899); }
.sm-alert-dot{
  position:absolute;top:-6px;right:-6px;width:20px;height:20px;border-radius:9999px;background:#f97316;
  display:flex;align-items:center;justify-content:center;
}
.channel-account-link {
  color: inherit;
  text-decoration: underline;
  word-break: break-all;
}

.status-dot{ width:10px;height:10px;border-radius:9999px;background:#9ca3af; }
.mono-ellipsis{ overflow:hidden;white-space:nowrap;text-overflow:ellipsis;max-width: 100%; }

.sm-avatar-wrap {
  position: relative;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
}

.sm-avatar {
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
}

.platform-badge {
  position: absolute;
  right: -20px;
  bottom: -2px;
  width: 20px !important;
  height: 20px !important;
  border-radius: 4px;
  background: none;
}

.sm-disconnect-dot {
  position: absolute;
  right: -3px;
  top: 2px;
  width: 8px;
  height: 8px;
  background: #ea580c;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.06);
}

.platform-select {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

@media (max-width: 1280px) {
  .platform-select {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 960px) {
  .platform-select {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .platform-select {
    grid-template-columns: 1fr;
  }
}
/* .custom-col {
  width: 100% !important;
} */

.integration-form-body {
  padding-top: 0;
}
.integration-form-actions {
  padding: 16px 24px;
}

.chip_posi { position: absolute; top: 8px; left: 8px; padding: 6px;z-index: 1; opacity: 1; }
</style>
